import { MessageSquare, Plus, Sparkles, Trash2, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ScrollArea, ScrollAreaScrollbar, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAIAssistant } from '@/contexts/ai-assistant-context';
import { cn } from '@/lib/utils';
import { AIAssistantChat } from './ai-assistant-chat';
import { AIAssistantInput } from './ai-assistant-input';
import { AIAssistantSuggestions } from './ai-assistant-suggestions';

export function AIAssistantPanel() {
  const { isOpen, closePanel, messages, sessions, currentSessionId, loadSession, startNewSession, deleteSession } =
    useAIAssistant();
  const panelRef = useRef<HTMLDialogElement>(null);
  const [sidebarWidth, setSidebarWidth] = useState(256);
  const location = useLocation();

  // Close panel when navigation occurs
  useEffect(() => {
    if (isOpen) {
      closePanel();
    }
  }, [location.pathname]);

  // Detect sidebar width changes
  useEffect(() => {
    const detectSidebarWidth = () => {
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        const width = sidebar.getBoundingClientRect().width;
        setSidebarWidth(width);
      }
    };

    detectSidebarWidth();

    const observer = new MutationObserver(detectSidebarWidth);
    const sidebar = document.querySelector('aside');
    if (sidebar) {
      observer.observe(sidebar, { attributes: true, attributeFilter: ['class'] });
    }

    window.addEventListener('resize', detectSidebarWidth);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', detectSidebarWidth);
    };
  }, []);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        closePanel();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closePanel]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(e.target as Node)) {
        const target = e.target as HTMLElement;
        if (target.classList.contains('ai-assistant-backdrop')) {
          closePanel();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closePanel]);

  const hasMessages = messages && messages.length > 0;

  const isDesktop = typeof window !== 'undefined' && window.innerWidth >= 1024;
  const panelLeft = isDesktop ? sidebarWidth : 0;
  const panelWidth = isDesktop ? `calc(100% - ${sidebarWidth}px)` : '100%';

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="ai-assistant-backdrop fixed bg-background/80 backdrop-blur-sm z-40 transition-opacity"
          style={{
            top: 0,
            left: panelLeft,
            right: 0,
            bottom: 0,
          }}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <dialog
        ref={panelRef}
        open={isOpen}
        className={cn(
          'fixed top-0 bottom-0 z-50 bg-card border-l shadow-lg m-0 p-0 max-w-none max-h-none',
          'transform transition-all duration-300 ease-in-out',
          'flex flex-col',
          isOpen ? 'translate-x-0' : 'translate-x-full',
        )}
        style={{
          left: panelLeft,
          width: panelWidth,
          right: 0,
          height: '100vh',
        }}
        aria-label="AI Assistant Panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 p-4 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">AI Assistant</h2>
              <p className="text-xs text-muted-foreground">Ask me anything about credit control</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <TooltipProvider delayDuration={300}>
              {sessions.length > 0 && (
                <div className="flex items-center gap-0.5 mr-1">
                  {sessions.slice(0, 10).map((session, index) => (
                    <Tooltip key={session.id}>
                      <TooltipTrigger asChild>
                        <div className="relative group">
                          <Button
                            variant={currentSessionId === session.id ? 'secondary' : 'ghost'}
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => loadSession(session.id)}
                            aria-label={`Load session: ${session.title ?? 'Session ' + String(index + 1)}`}
                          >
                            <MessageSquare className="h-3.5 w-3.5" />
                          </Button>
                          <button
                            className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteSession(session.id);
                            }}
                            aria-label={`Delete session: ${session.title ?? 'Session ' + String(index + 1)}`}
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[200px]">
                        <p className="text-xs font-medium truncate">{session.title ?? `Session ${index + 1}`}</p>
                        <p className="text-xs text-muted-foreground">
                          {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" onClick={startNewSession} aria-label="Start new session">
                    <Plus className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="text-xs">New session</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button
              variant="ghost"
              size="icon"
              onClick={closePanel}
              title="Close panel"
              aria-label="Close AI Assistant panel"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Quick Actions / Suggestions */}
        {!hasMessages && (
          <div className="p-4 border-b bg-muted/30">
            <AIAssistantSuggestions />
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <ScrollAreaViewport className="h-full">
              <div className="p-4">
                <AIAssistantChat />
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </div>

        {/* Input */}
        <div className="border-t bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
          <AIAssistantInput autoFocus={isOpen} />
        </div>
      </dialog>
    </>
  );
}
